import React, { useState, useEffect } from 'react'
import {
  Grid,
  Avatar,
  Button,
  MenuItem,
  FormGroup,
  TextField,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Theme,
  useTheme,
} from '@mui/material'
import { makeStyles } from '@mui/styles';
import { Formik, Form, Field, ErrorMessage } from 'formik'
import 'date-fns'
import DateFnsUtils from '@date-io/date-fns'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers'
import {
  getCurrentProfile,
  createOrUpdateProfile,
} from '../actions/profileStudent'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import BackdropSpinner from '../components/ui/BackdropSpinner'
import { Cloudinary } from '@cloudinary/url-gen'
import { RootState } from '../reducers';
import { StudentProfileFormData } from '../types';
import styled from "@emotion/styled";

const useStyles = makeStyles((theme: Theme) => ({
  rowContainer: {
    paddingLeft: '1.5em',
    paddingRight: '1.5em',
    [theme.breakpoints.up('sm')]: {
      paddingLeft: '3em',
      paddingRight: '3em',
    },
    [theme.breakpoints.up('md')]: {
      paddingLeft: '5em',
      paddingRight: '5em',
    },
  },
  formControl: {
    marginBottom: '1.5em',
    width: '100%',
  },
}))

interface Props {
  history: any
}

const FormControlWrapper = styled(Grid)(
  {
    marginBottom: '1.5em'
  }
)

const ProfileFormScreen: React.FC<Props> = ({ history }) => {
  const [formData, setFormData] = useState<StudentProfileFormData | null>(null);
  const [previewImageAvatarSource, setPreviewImageAvatarSource] = useState<string | ArrayBuffer | null>(null)
  const [cldImageAvatar, setCldImageAvatar] = useState<any>('');

  const classes = useStyles()

  const theme = useTheme()
  const matchesSM = useMediaQuery(theme.breakpoints.up('sm'))
  const matchesLG = useMediaQuery(theme.breakpoints.up('lg'))

  const dispatch = useDispatch()

  const initialValues = {
    imageAvatar: null,
    selectedImageAvatar: null,
    address: '',
    dateOfBirth: null,
    gender: '',
    englishLevel: '0',
    introduction: '',
    skypeId: '',
    phoneNumber: '',
  }

  const profileStudent = useSelector((state: RootState) => state.profileStudent)
  const { loading, profile: profileUser } = profileStudent

  const cld = new Cloudinary({
    cloud: {
      cloudName: 'youspeak',
    },
  })

  if (profileUser && profileUser.imageAvatar && !cldImageAvatar) {
    const cldStudentAvatar = cld.image(profileUser.imageAvatar)
    const cldRes = cldStudentAvatar.toURL()
    setCldImageAvatar(cldRes)
  }

  const validationSchema = yup.object().shape({
    dateOfBirth: yup.date().nullable(),
    phoneNumber: yup
      .string()
      .typeError('Vui lòng điền số điện thoại')
      .required('Vui lòng điền số điện thoại'),
    skypeId: yup
      .string()
      .typeError('Vui lòng điền Skype ID của bạn')
      .required('Vui lòng điền Skype ID của bạn'),
  })

  useEffect(() => {
    if (!profileUser) {
      dispatch(getCurrentProfile())
    }
    if (!loading && profileUser) {
      setFormData({
        imageAvatar: profileUser.imageAvatar || null,
        selectedImageAvatar: null,
        address: profileUser.address || '',
        dateOfBirth: profileUser.dateOfBirth || null,
        gender: profileUser.gender || '',
        englishLevel: profileUser.englishLevel || '0',
        introduction: profileUser.introduction || '',
        skypeId: profileUser.skypeId || '',
        phoneNumber: profileUser.phoneNumber || '',
      })
    }
  }, [profileUser, loading, dispatch])

  return <>
    {loading ? (
      <BackdropSpinner />
    ) : (
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        className={classes.rowContainer}
        style={{ margin: '7em 0 2em' }}
      >
        <Grid item>
          <Typography
            variant="h5"
            gutterBottom
            style={{
              fontWeight: 600,
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
          >
            Chỉnh sửa Profile
          </Typography>
        </Grid>
        <Grid
          item
          style={{ minWidth: matchesLG ? '60%' : matchesSM ? '80%' : '100%' }}
        >
          <Formik
            enableReinitialize
            initialValues={formData || initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              dispatch(
                createOrUpdateProfile(
                  values,
                  history,
                  profileUser ? true : false
                )
              )
            }}
          >
            {({ values, errors, isSubmitting, setFieldValue, isValid }) =>
              loading ? (
                <BackdropSpinner />
              ) : (
                <Form>
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Grid item>
                      <Avatar
                        src={
                          previewImageAvatarSource
                            ? previewImageAvatarSource
                            : cldImageAvatar
                              ? cldImageAvatar
                              : ''
                        }
                        style={{ width: '4em', height: '4em' }}
                        alt="avatar-image"
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">Đổi ảnh profile</Typography>
                    </Grid>
                    <Grid item style={{ marginBottom: '2em' }}>
                      <Button
                        variant="contained"
                        component="label"
                        color="primary"
                        style={{ color: 'white', fontWeight: 500 }}
                      >
                        Tải file ảnh
                        <input
                          type="file"
                          style={{ display: 'none' }}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            if (!event.currentTarget.files) return;

                            const file = event.currentTarget.files[0]

                            const reader = new FileReader()
                            reader.readAsDataURL(file)
                            reader.onloadend = () => {
                              setPreviewImageAvatarSource(reader.result)
                            }

                            setFieldValue('selectedImageAvatar', file)
                          }}
                        />
                      </Button>
                    </Grid>

                    <Grid
                      item
                    >
                      <Card>
                        <CardContent>
                          <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Grid item>
                              <Typography
                                variant="h6"
                                style={{ textAlign: 'center' }}
                              >
                                Thông tin cá nhân
                              </Typography>
                            </Grid>

                            <FormControlWrapper item className={classes.formControl}>
                              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDatePicker
                                  autoComplete="off"
                                  id="date-picker-dialog"
                                  label="Ngày tháng năm sinh"
                                  inputVariant="outlined"
                                  format="dd/MM/yyyy"
                                  clearable
                                  value={values.dateOfBirth}
                                  onChange={(value) =>
                                    setFieldValue('dateOfBirth', value)
                                  }
                                  KeyboardButtonProps={{
                                    'aria-label': 'change date',
                                  }}
                                />
                              </MuiPickersUtilsProvider>
                            </FormControlWrapper>

                            <FormControlWrapper item className={classes.formControl}>
                              <FormGroup>
                                <Field
                                  autoComplete="off"
                                  name="gender"
                                  select
                                  label="Giới tính"
                                  as={TextField}
                                >
                                  <MenuItem value="male">Nam</MenuItem>
                                  <MenuItem value="female">Nữ</MenuItem>
                                </Field>
                              </FormGroup>
                            </FormControlWrapper>

                            <FormControlWrapper item className={classes.formControl}>
                              <FormGroup>
                                <Field
                                  autoComplete="off"
                                  name="address"
                                  as={TextField}
                                  label="Địa chỉ"
                                />
                                <ErrorMessage name="address" />
                              </FormGroup>
                            </FormControlWrapper>

                            <FormControlWrapper item className={classes.formControl}>
                              <FormGroup>
                                <Field
                                  autoComplete="off"
                                  name="englishLevel"
                                  select
                                  label="Trình độ tiếng Anh"
                                  as={TextField}
                                >
                                  <MenuItem value={0}>Chưa biết</MenuItem>
                                  <MenuItem value={1}>1 / 5</MenuItem>
                                  <MenuItem value={2}>2 / 5</MenuItem>
                                  <MenuItem value={3}>3 / 5</MenuItem>
                                  <MenuItem value={4}>4 / 5</MenuItem>
                                  <MenuItem value={5}>5 / 5</MenuItem>
                                </Field>
                              </FormGroup>
                            </FormControlWrapper>

                            <FormControlWrapper item className={classes.formControl}>
                              <FormGroup>
                                <Field
                                  autoComplete="off"
                                  name="skypeId"
                                  as={TextField}
                                  label="Skype ID"
                                  error={errors.skypeId}
                                />
                                <ErrorMessage name="skypeId">
                                  {(msg) => (
                                    <div style={{ color: 'red' }}>{msg}</div>
                                  )}
                                </ErrorMessage>
                              </FormGroup>
                            </FormControlWrapper>

                            <FormControlWrapper item className={classes.formControl}>
                              <FormGroup>
                                <Field
                                  autoComplete="off"
                                  name="phoneNumber"
                                  as={TextField}
                                  label="Số điện thoại"
                                  type="text"
                                  error={errors.phoneNumber}
                                />
                                <ErrorMessage name="phoneNumber">
                                  {(msg) => (
                                    <div style={{ color: 'red' }}>{msg}</div>
                                  )}
                                </ErrorMessage>
                              </FormGroup>
                            </FormControlWrapper>

                            <FormControlWrapper item className={classes.formControl}>
                              <FormGroup>
                                <Field
                                  autoComplete="off"
                                  name="introduction"
                                  as={TextField}
                                  label="Thông tin thêm"
                                  multiline
                                  rows={4}
                                />
                                <ErrorMessage name="introduction" />
                              </FormGroup>
                            </FormControlWrapper>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid
                      item
                      container
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      style={{ marginTop: '1em' }}
                    >
                      <Grid item>
                        <Button
                          component={Link}
                          variant="contained"
                          color="primary"
                          type="submit"
                          style={{
                            color: 'white',
                            fontWeight: 600,
                            marginLeft: '0.5em',
                          }}
                          to="/dashboard"
                        >
                          Quay lại
                        </Button>
                      </Grid>

                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={!isValid || isSubmitting}
                          style={{ color: 'white', fontWeight: 600 }}
                        >
                          Lưu lại
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )
            }
          </Formik>
        </Grid>
      </Grid>
    )}
  </>;
}

export default ProfileFormScreen
